import React, { useEffect, useState } from 'react';
import { Copy, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { googleAdsService } from '@/services/google-ads.service';
import { GoogleAdsNotice } from './GoogleAdsShell';
import { errMsg, Spinner } from './youtube/youtubeShared';

type Candidate = Awaited<ReturnType<typeof googleAdsService.reusableAds>>[number];

export function ReuseAdDialog({ accountUid, group, onClose, onSaved }: {
  accountUid: string; group: { id: string; name: string }; onClose: () => void; onSaved: () => void;
}) {
  const [rows, setRows] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Candidate | null>(null);
  const [saving, setSaving] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true); setError(''); setSelected(null);
    googleAdsService.reusableAds(accountUid, group.id).then((data) => { if (active) setRows(data); })
      .catch((e) => { if (active) setError(errMsg(e)); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [accountUid, group.id, revision]);
  const save = async () => {
    if (!selected) return;
    setSaving(true); setError('');
    try {
      await googleAdsService.reuseAd(accountUid, group.id, { sourceAdGroupId: selected.adGroupId, sourceAdId: selected.adId });
      toast.success(selected.type === 'APP_AD' ? 'App ad copied. Its parent remains paused.' : 'Ad copied and saved paused.'); onSaved();
    } catch (e) { setError(errMsg(e)); } finally { setSaving(false); }
  };
  const filtered = rows.filter((r) => `${r.name} ${r.campaignName} ${r.adId}`.toLowerCase().includes(query.toLowerCase()));
  return <Modal open onClose={() => { if (!saving) onClose(); }} title="Use an existing ad" widthClass="max-w-2xl">
    <p className="mb-4 text-sm text-text-secondary">Copy a compatible ad into <strong>{group.name}</strong>. The copy goes through Google's review and stays paused. App ads follow the parent, so the destination campaign or ad group must be paused. Your original ad stays as it is.</p>
    <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search ads or campaigns…" iconLeft={<Search size={15} />} />
    {error && <div className="mt-3"><GoogleAdsNotice error title="Could not load or copy this ad">{error}<Button size="sm" variant="secondary" onClick={() => setRevision((v) => v + 1)}>Retry</Button></GoogleAdsNotice></div>}
    <div className="my-3 max-h-72 space-y-2 overflow-y-auto">
      {loading ? <div className="flex items-center gap-2 py-6 text-sm text-text-muted"><Spinner size={16} /> Loading compatible ads…</div>
        : filtered.length === 0 && !error ? <p className="py-6 text-center text-sm text-text-muted">{query ? 'No ads match your search.' : 'No compatible ads in other ad groups yet. App ads must promote the same app.'}</p>
        : filtered.map((r) => <button key={`${r.adGroupId}-${r.adId}`} type="button" disabled={saving} aria-pressed={selected === r} onClick={() => setSelected(r)}
          className={`w-full rounded-lg border p-3 text-left transition-colors ${selected === r ? 'border-primary bg-primary-soft' : 'border-border hover:bg-surface-alt'}`}>
          <div className="text-sm font-semibold text-text-primary">{r.name}</div>
          <div className="mt-1 text-xs text-text-muted">{r.campaignName} / {r.adGroupName}</div>
          <div className="mt-1 line-clamp-2 text-xs text-text-secondary">{r.descriptions.join(' · ')}</div>
        </button>)}
    </div>
    <div className="flex justify-end gap-2 border-t border-border pt-3">
      <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
      <Button iconLeft={<Copy size={14} />} disabled={!selected || loading} loading={saving} onClick={save}>Use selected ad</Button>
    </div>
  </Modal>;
}
